import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { CardExtraVariant, ServiceType } from '../../types';
import { Money as MoneyType } from '../../../Money/types';
import { useTheme } from '../../../theme';
import AggregatorService from '../../../CheckoutAdditionalService/CheckoutAdditionalService/Aggregator/AggregatorService';
import Button from '../../../BaseComponents/Button';
import Money from '../../../Money';
import { CdnIconNames, CardExtraIcon } from '@shared/ui';
import { SafeHtml } from '@shared/ui';
import { buildDataAttributes } from '@shared/lib/dom';

export interface Props {
	type: ServiceType;
	insuranceCodes?: string[];
	variant?: CardExtraVariant;
	icon?: string;
	aggregationIcons?: React.ReactNode[] | CdnIconNames[];
	coverClassName?: string;
	content: React.ReactNode;
	additionalControls?: React.ReactNode;
	title?: string;
	description?: string;
	style?: {
		headerBg?: string;
		displayOrder?: number;
	};
	options?: string[];
	minPrice?: MoneyType;
	totalPrice?: MoneyType;
	isSelected?: boolean;
	isConfirmed?: boolean;
	isReadonly?: boolean;
	isLoading?: boolean;
	onChangeService: () => void;
	onToggleService?: () => void;
	onViewLoyaltyStatus?: () => void;
	hideFooterControls?: boolean;
	additionalFooterControls?: React.ReactNode;
	additionalText?: React.ReactNode;
}

const CardExtraContent: React.FC<Props> = ({
	type,
	insuranceCodes,
	variant,
	icon,
	aggregationIcons,
	coverClassName,
	content,
	additionalControls,
	title,
	description,
	style,
	options,
	minPrice,
	totalPrice,
	isSelected,
	isConfirmed,
	isReadonly,
	isLoading,
	onChangeService,
	onToggleService,
	onViewLoyaltyStatus,
	hideFooterControls,
	additionalFooterControls,
	additionalText
}) => {
	const { t } = useTranslation('CardExtra');
	const { CardExtraContent: theme } = useTheme('CardExtra');

	const serviceTitle = title ?? t(`ServiceTitle.${type}`);
	const serviceDescription = description ?? t(`ServiceDescription.${type}`);

	const buttonClasses = cn(theme.button, {
		[theme.button_add]: !isSelected,
		[theme.button_change]: isSelected
	});

	const buttonText = useMemo(() => {
		if (!isSelected) {
			return t('Add');
		}
		if (isConfirmed || isReadonly) {
			return t('More details');
		}

		return t(onToggleService ? 'Remove' : 'Edit');
	}, [isSelected, isConfirmed, isReadonly, onToggleService]);

	const price = useMemo(() => {
		return isSelected ? (totalPrice?.amount > 0 ? totalPrice : null) : minPrice;
	}, [totalPrice, minPrice, isSelected]);

	const onClickButton = onToggleService ?? onChangeService;

	if (variant === 'aggregator') {
		const { Loyalty: loyaltyTheme } = useTheme('CheckoutAdditionalService');

		const isLoyaltySelected = type === 'loyalty' && isSelected;
		const aggregatorButtonAction = isLoyaltySelected && onViewLoyaltyStatus ? onViewLoyaltyStatus : onClickButton;
		const aggregatorButtonText = isLoyaltySelected ? t('View status') : buttonText;

		return (
			<AggregatorService
				className={cn(theme.root, theme.root_aggregator, {
					[loyaltyTheme.loyalty]: type === 'loyalty'
				})}
				header={serviceTitle}
				description={serviceDescription}
				coverBackgroundUrl={icon}
				coverBackgroundColor={style?.headerBg}
				coverClassName={coverClassName}
				aggregationIcons={aggregationIcons}
				displayOrder={style?.displayOrder}
				buttons={[{ text: aggregatorButtonText, action: aggregatorButtonAction, isLoading }]}
				hideFooterButtons={hideFooterControls}
				isSelected={isSelected}
				isLoading={isLoading}
				addClick={aggregatorButtonAction}
			>
				{content}
			</AggregatorService>
		);
	}

	return (
		<div
			className={cn(theme.root, {
				[theme.selected]: isSelected,
				[theme.root_seats]: type === 'seats',
				[theme.root_baggage]: type === 'baggage',
				[theme.root_baggageDelivery]: type === 'baggage-delivery',
				[theme.root_animal]: type === 'animal',
				[theme.root_meal]: type === 'meal',
				[theme.root_businessLounge]: type === 'business-lounge',
				[theme.root_priorityBoarding]: type === 'priority-boarding',
				[theme.root_insurance]: type === 'insurance',
				[theme.root_transfer]: type === 'transfer',
				[theme.root_capsuleHotel]: type === 'capsule-hotel',
				[theme.root_loyalty]: type === 'loyalty',
				[theme.root_upgrade]: type === 'upgrade',
				[theme.root_sms]: type === 'sms',
				[theme.root_refundableTicket]: type === 'refundable-ticket',
				[theme.root_misc]: type === 'misc'
			})}
			{...buildDataAttributes({
				'service-type': type,
				'test-id': 'card-extra',
				'insurance-code': insuranceCodes?.[0]
			})}
			style={{ order: style?.displayOrder }}
		>
			<div className={theme.header} style={{ background: style?.headerBg }}>
				<div className={theme.header__title}>{serviceTitle}</div>

				<div className={cn(theme.header__icon, { [theme.loyalty__icon]: type === 'loyalty' })}>
					<CardExtraIcon type={type} iconUrl={icon} />
				</div>
			</div>

			<div className={theme.content}>
				{content}

				{!isSelected && <SafeHtml tag="div" className={theme.description} html={serviceDescription} />}

				{!!additionalText && (
					<SafeHtml tag="div" className={theme.additionalText} html={additionalText as string} />
				)}

				{!!options?.length &&
					options.map((option, index) => (
						<SafeHtml key={index} tag="div" className={theme.options} html={option} />
					))}
			</div>

			{additionalControls && <div className={theme.controls}>{additionalControls}</div>}

			<div className={theme.footer}>
				{price && type !== 'loyalty' && (
					<span className={theme.footer__price}>
						<span>
							{t(isSelected ? 'total' : 'from')}:
							<Money money={price} />
						</span>
					</span>
				)}

				{!hideFooterControls &&
					(type === 'loyalty' ? (
						onViewLoyaltyStatus && (
							<Button
								className={buttonClasses}
								variant={isSelected ? 'text' : 'optionAction'}
								onClick={isSelected ? onViewLoyaltyStatus : onChangeService}
								isLoading={isLoading}
							>
								{t(isSelected ? 'View status' : 'Register')}
							</Button>
						)
					) : (
						<Button
							className={buttonClasses}
							variant={isSelected ? 'optionEdit' : 'optionAction'}
							data-test-id="card-extra-action"
							data-action={isSelected ? 'change' : 'add'}
							onClick={onClickButton}
							isLoading={isLoading}
						>
							{buttonText}
						</Button>
					))}

				{additionalFooterControls}
			</div>
		</div>
	);
};

export default CardExtraContent;
